import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil, Users, BookOpen } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", party_type: "both", gstin: "", address: "", state: "", phone: "", email: "", opening_balance: "" };

export default function Parties() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [parties, setParties] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");

  const load = () => api.get("/parties").then((r) => setParties(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, opening_balance: p.opening_balance ?? "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, opening_balance: parseFloat(form.opening_balance || 0), gstin: form.gstin.toUpperCase() };
    try {
      if (editing) await api.put(`/parties/${editing.id}`, payload); else await api.post("/parties", payload);
      toast.success(editing ? "Party updated" : "Party added"); setOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => {
    try { await api.delete(`/parties/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const shown = parties.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.gstin || "").toLowerCase().includes(search.toLowerCase()));
  const f = (key, label, props = {}) => (
    <div className="space-y-2"><Label>{label}</Label>
      <Input data-testid={`party-${key}-input`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...props} /></div>
  );

  return (
    <div className="space-y-6 animate-fade-up" data-testid="parties-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Parties</h1>
          <p className="text-muted-foreground mt-1">Customers & vendors with GST details and due balances.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-party-btn" onClick={openNew} className="bg-sky-500 hover:bg-sky-600 text-white gap-2"><Plus className="h-4 w-4" /> Add Party</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit Party" : "Add Party"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {f("name", "Party Name", { required: true })}
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={form.party_type} onValueChange={(v) => setForm({ ...form, party_type: v })}>
                    <SelectTrigger data-testid="party-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="customer">Customer</SelectItem><SelectItem value="vendor">Vendor</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
                  </Select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {f("gstin", "GSTIN", { placeholder: "22AAAAA0000A1Z5", maxLength: 15 })}
                {f("state", "State", { placeholder: "Uttar Pradesh", required: true })}
              </div>
              {f("address", "Address")}
              <div className="grid grid-cols-2 gap-4">
                {f("phone", "Phone")}
                {f("email", "Email", { type: "email" })}
              </div>
              {f("opening_balance", "Opening Balance (₹, + receivable / − payable)", { type: "number", step: "0.01" })}
              <DialogFooter><Button type="submit" data-testid="party-submit-btn" className="bg-sky-500 hover:bg-sky-600 text-white">{editing ? "Update" : "Save Party"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Input data-testid="party-search-input" placeholder="Search by name or GSTIN…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />

      <Card className="bg-card border-border"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Type</th><th className="px-4 py-3 font-medium">GSTIN</th>
            <th className="px-4 py-3 font-medium">State</th><th className="px-4 py-3 font-medium">Phone</th>
            <th className="px-4 py-3 font-medium text-right">Receivable</th><th className="px-4 py-3 font-medium text-right">Payable</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody data-testid="parties-table-body">
            {shown.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground"><Users className="h-10 w-10 mx-auto mb-2 opacity-40" />No parties yet.</td></tr>
            ) : shown.map((p) => (
              <tr key={p.id} className="border-b border-border/50 hover:bg-white/5" data-testid={`party-row-${p.id}`}>
                <td className="px-4 py-3 font-medium"><Link to={`/parties/${p.id}`} className="hover:text-sky-400" data-testid={`party-ledger-link-${p.id}`}>{p.name}</Link></td>
                <td className="px-4 py-3"><Badge variant="outline" className="capitalize text-sky-300 border-sky-500/30">{p.party_type}</Badge></td>
                <td className="px-4 py-3 font-mono-sku text-xs">{p.gstin || "—"}</td>
                <td className="px-4 py-3">{p.state || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.phone || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-sku text-emerald-400" data-testid={`party-receivable-${p.id}`}>{p.receivable ? fmt(p.receivable) : "—"}</td>
                <td className="px-4 py-3 text-right font-mono-sku text-amber-400" data-testid={`party-payable-${p.id}`}>{p.payable ? fmt(p.payable) : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button asChild size="icon" variant="ghost" className="h-8 w-8"><Link to={`/parties/${p.id}`} title="Ledger"><BookOpen className="h-4 w-4" /></Link></Button>
                  {isAdmin && <Button size="icon" variant="ghost" data-testid={`party-edit-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8"><Pencil className="h-4 w-4" /></Button>}
                  {isAdmin && <Button size="icon" variant="ghost" data-testid={`party-delete-${p.id}`} onClick={() => del(p.id)} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
